/** 真圖素材登記：所有圖標、字靈獸、徽章、地圖與背景都是畫出來的點陣圖（public/assets/art/v22），不用線條圖示。 */
import type { Tier } from "./types";
import { hashString } from "./rng";

export const ART = "/assets/art/v22";

export const art = {
  logo: `${ART}/logo.webp`,
  dial: `${ART}/dial.webp`,
  cardback: `${ART}/cardback.webp`,
  candle: `${ART}/candle.webp`,
  mana: `${ART}/mana.webp`,
  star: `${ART}/star.webp`,
  crown: `${ART}/crown.webp`,
  nav: {
    home: `${ART}/nav-home.webp`,
    journey: `${ART}/nav-journey.webp`,
    board: `${ART}/nav-board.webp`,
    codex: `${ART}/nav-codex.webp`,
    me: `${ART}/nav-me.webp`,
  },
  rank: [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => `${ART}/rank-${i}.webp`),
  gem: { N: `${ART}/gem-N.webp`, R: `${ART}/gem-R.webp`, SR: `${ART}/gem-SR.webp`, SSR: `${ART}/gem-SSR.webp` } as Record<Tier, string>,
  mode: { daily: `${ART}/mode-daily.webp`, free: `${ART}/mode-free.webp`, wrong: `${ART}/mode-wrong.webp` },
  bg: {
    home: `${ART}/bg-home.webp`,
    board: `${ART}/bg-board.webp`,
    codex: `${ART}/bg-codex.webp`,
    me: `${ART}/bg-me.webp`,
    battle: `${ART}/bg-battle.webp`,
    result: `${ART}/bg-result.webp`,
  },
  node: { off: `${ART}/node-off.webp`, lit: `${ART}/node-lit.webp`, clear: `${ART}/node-clear.webp` },
  skill: { zhuzhao: `${ART}/skill-zhuzhao.webp`, zhishui: `${ART}/skill-zhishui.webp`, shenji: `${ART}/skill-shenji.webp` },
};

export interface Beast {
  id: string;
  name: string;
  tier: Tier;
  src: string;
}

/** 字靈獸：每個稀有度四種形態，依字靈 id 固定分配（同一隻字靈永遠是同一獸）。 */
export const BEASTS: Record<Tier, Beast[]> = {
  N: [
    { id: "n-fox", name: "墨狐", tier: "N", src: `${ART}/beast-n-fox.webp` },
    { id: "n-crane", name: "紙鶴", tier: "N", src: `${ART}/beast-n-crane.webp` },
    { id: "n-rabbit", name: "燈兔", tier: "N", src: `${ART}/beast-n-rabbit.webp` },
    { id: "n-lion", name: "苔獅", tier: "N", src: `${ART}/beast-n-lion.webp` },
  ],
  R: [
    { id: "r-qilin", name: "青麒", tier: "R", src: `${ART}/beast-r-qilin.webp` },
    { id: "r-turtle", name: "玄龜", tier: "R", src: `${ART}/beast-r-turtle.webp` },
    { id: "r-tiger", name: "雲虎", tier: "R", src: `${ART}/beast-r-tiger.webp` },
    { id: "r-crow", name: "雷烏", tier: "R", src: `${ART}/beast-r-crow.webp` },
  ],
  SR: [
    { id: "sr-fox9", name: "九尾", tier: "SR", src: `${ART}/beast-sr-fox9.webp` },
    { id: "sr-phoenix", name: "朱雀", tier: "SR", src: `${ART}/beast-sr-phoenix.webp` },
    { id: "sr-wolf", name: "影狼", tier: "SR", src: `${ART}/beast-sr-wolf.webp` },
    { id: "sr-taotie", name: "饕餮", tier: "SR", src: `${ART}/beast-sr-taotie.webp` },
  ],
  SSR: [
    { id: "ssr-dragon", name: "金龍", tier: "SSR", src: `${ART}/beast-ssr-dragon.webp` },
    { id: "ssr-phoenix", name: "火鳳", tier: "SSR", src: `${ART}/beast-ssr-phoenix.webp` },
    { id: "ssr-baize", name: "白澤", tier: "SSR", src: `${ART}/beast-ssr-baize.webp` },
    { id: "ssr-yinglong", name: "應龍", tier: "SSR", src: `${ART}/beast-ssr-yinglong.webp` },
  ],
};

export function beastById(id: string): Beast | undefined {
  for (const list of Object.values(BEASTS)) for (const b of list) if (b.id === id) return b;
  return undefined;
}

export function beastFor(card: { id: string; tier: Tier }): Beast {
  const list = BEASTS[card.tier] || BEASTS.N;
  return list[hashString(`beast|${card.id}`) % list.length];
}

export interface Region {
  /** 域的識別鍵（圖鑑分區、地圖 key） */
  id: string;
  /** 這個域涵蓋的冊別（一域可含多冊，例如貢院含北京卷與指考） */
  books: string[];
  /** 附加域（2026-10-31 杏壇書院、龍門貢院）：關卡計入通關數，不計入段位的「全通域」 */
  bonus?: boolean;
  /** 異世界地名 */
  name: string;
  /** 對應的冊別（副題） */
  sub: string;
  map: string;
  guardian: string;
  accent: string;
}

/** 旅程七域：七冊融成一張連續的古靈異地圖，由啟蒙竹谷一路到蓬萊海疆。 */
export const REGIONS: Region[] = [
  { id: "ms", books: ["ms"], name: "啟蒙竹谷", sub: "初中古詩文", map: `${ART}/map-ms.webp`, guardian: "n-fox", accent: "#7fb77e" },
  { id: "v1", books: ["v1"], name: "松風瀑嶺", sub: "必修上", map: `${ART}/map-v1.webp`, guardian: "r-tiger", accent: "#d9a441" },
  { id: "v2", books: ["v2"], name: "赤壁江峽", sub: "必修下", map: `${ART}/map-v2.webp`, guardian: "r-turtle", accent: "#c9533a" },
  { id: "v3", books: ["v3"], name: "寒關雪隘", sub: "選必上", map: `${ART}/map-v3.webp`, guardian: "sr-wolf", accent: "#8fb8e6" },
  { id: "v4", books: ["v4"], name: "幽陵燈谷", sub: "選必中", map: `${ART}/map-v4.webp`, guardian: "sr-fox9", accent: "#b48ef1" },
  { id: "v5", books: ["v5"], name: "星海仙島", sub: "選必下", map: `${ART}/map-v5.webp`, guardian: "ssr-phoenix", accent: "#f0cf7a" },
  { id: "tw", books: ["tw"], name: "蓬萊海疆", sub: "臺灣核心古文", map: `${ART}/map-tw.webp`, guardian: "ssr-yinglong", accent: "#5fc9c0" },
  // 附加兩域（站長 2026-10-31：考靈域與論語域都做）：在地圖上、計通關數，不計入全通域；杏壇＝孔子講學處，貢院＝科舉試場
  { id: "ly", books: ["ly"], name: "杏壇書院", sub: "論語二十篇", map: `${ART}/map-ly.webp`, guardian: "ssr-baize", accent: "#c9a76a", bonus: true },
  { id: "exam", books: ["bj", "ceec"], name: "龍門貢院", sub: "北京卷・指考真題", map: `${ART}/map-exam.webp`, guardian: "r-qilin", accent: "#e07a4f", bonus: true },
];

export function regionOf(book: string): Region | undefined {
  return REGIONS.find((r) => r.books.includes(book));
}

/** 預載一批圖（切頁前先暖快取，避免背景閃白）。 */
export function warm(srcs: string[]): void {
  if (typeof Image === "undefined") return;
  for (const s of srcs) {
    const im = new Image();
    im.decoding = "async";
    im.src = s;
  }
}
